import axios from "axios";
import { useEffect, useState } from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import BlogList from "./BlogList";
import BlogSidebar from "./BlogSidebar";
import Talks from "./Talks";

const Blog = () => {
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]); // Store all tags
  const [filteredTags, setFilteredTags] = useState([]); // Store filtered tags
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("blogs");
  const location = useLocation();
  const navigate = useNavigate();

  // Parse category and tags from URL on component mount and when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromUrl = params.get("category");
    const tagsFromUrl = params.get("tags")?.split(",").filter(Boolean) || [];
    const sectionFromUrl = params.get("section") || "blogs";

    setSelectedCategory(categoryFromUrl);
    setSelectedTags(tagsFromUrl);
    setActiveSection(sectionFromUrl);
  }, [location]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesResponse, tagsResponse] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_BASE_API_URL}/api/blogs/categories/`,
          ),
          axios.get(`${process.env.REACT_APP_BASE_API_URL}/api/blogs/tags/`),
        ]);
        setCategories(categoriesResponse.data);
        setAllTags(tagsResponse.data);
        setFilteredTags(tagsResponse.data); // Initially show all tags
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Update available tags when category changes
  useEffect(() => {
    const fetchTagsForCategory = async () => {
      try {
        if (selectedCategory) {
          // Fetch posts or talks for the selected category based on active section
          const endpoint =
            activeSection === "talks"
              ? `${process.env.REACT_APP_BASE_API_URL}/api/blogs/presentations/`
              : `${process.env.REACT_APP_BASE_API_URL}/api/blogs/`;

          const response = await axios.get(endpoint, {
            params: { category: selectedCategory },
          });

          // Get unique tags from the posts
          const tagsInCategory = new Set();
          response.data.forEach((post) => {
            post.tags.forEach((tag) => {
              tagsInCategory.add(tag.slug);
            });
          });

          // Filter all tags to only show those used in the category
          const filteredTags = allTags.filter((tag) =>
            tagsInCategory.has(tag.slug),
          );
          setFilteredTags(filteredTags);
        } else {
          // If no category is selected, show all tags
          setFilteredTags(allTags);
        }
      } catch (error) {
        console.error("Error fetching tags for category:", error);
      }
    };

    fetchTagsForCategory();
  }, [selectedCategory, allTags, activeSection]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedTags([]); // Clear selected tags when changing category
    const params = new URLSearchParams(location.search);
    if (category) {
      params.set("category", category);
    } else {
      params.delete("category");
    }
    navigate(`/blogs?${params.toString()}`);
  };

  const handleTagSelect = (tag) => {
    let newTags;
    if (selectedTags.includes(tag)) {
      newTags = selectedTags.filter((t) => t !== tag);
    } else {
      newTags = [...selectedTags, tag];
    }
    setSelectedTags(newTags);

    const params = new URLSearchParams(location.search);
    if (newTags.length > 0) {
      params.set("tags", newTags.join(","));
    } else {
      params.delete("tags");
    }

    navigate(`/blogs?${params.toString()}`);
  };

  const handleSectionSelect = (section) => {
    setActiveSection(section);
    const params = new URLSearchParams(location.search);
    params.set("section", section);
    navigate(`/blogs?${params.toString()}`);
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <section className="portfolio-section section-band-1">
      <Helmet>
        <title>Blog &amp; Presentations — Kaustubh Harapanahalli</title>
        <meta
          name="description"
          content="Blog posts and presentations by Kaustubh Harapanahalli on machine learning, research, and more."
        />
      </Helmet>
      <Container>
        <Row>
          <Col md={3}>
            <BlogSidebar
              categories={categories}
              tags={filteredTags} // Pass filtered tags instead of all tags
              selectedCategory={selectedCategory}
              selectedTags={selectedTags}
              onCategorySelect={handleCategorySelect}
              onTagSelect={handleTagSelect}
              activeSection={activeSection}
              onSectionSelect={handleSectionSelect}
            />
          </Col>
          <Col md={9}>
            {activeSection === "blogs" ? (
              <BlogList
                selectedCategory={selectedCategory}
                selectedTags={selectedTags}
              />
            ) : (
              <Talks
                selectedCategory={selectedCategory}
                selectedTags={selectedTags}
              />
            )}
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Blog;
